import React, { useState } from 'react'
import { DashboardService } from '../../../../Api/EmploymentEquity/Dashboard/DashboardService';
import { EmploymentEquityDashboardDto } from '../../../../Model/Dto/EmploymentEquity/Dashboard';
import { GenderList } from '../../../../Model/Enum/MultiSelectOptions/GenderOptions';
import { OccupationalLevelDisplayList } from '../../../../Model/Enum/MultiSelectOptions/OccupationallevelOptions';
import { RaceList } from '../../../../Model/Enum/MultiSelectOptions/RaceOptions';
import { mapEmployeeHeadcountToDoubleColumnChartData } from '../../../../Model/Mappers/EmploymentEquityMapper';
import { IMultiSelectOptions } from '../../MultiSelect/MultiSelect';
import { ChartFilter } from '../../Popovers/ChartFilter';
import { DoubleColumnChart } from '../Base/DoubleColumnChart/DoubleColumnChart';
import { AlertMessage } from '../../Alerts/alertMessage';
import { getDateWithoutTime, userFriendlyDate } from '../../../../../Helpers/DateFormatter';
import { Box, ClickAwayListener, IconButton, Popper } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { MDBox } from '../../MDBox';
import { MDButton } from '../../MDButton';
import { OccupationalLevelTypes } from '../../../../Model/Enum/BEE123Enums';

interface IProps {
  scorecardId?: string;
  scorecardTypeId?: string;
  chartOptions?: any;
  dashboardLevelOnClick?: (chartLabel: string) => void;
  sourceScorecardIds?: string[];
}

export const EmployementEquity1MultiLevelChart = (props: IProps) => {
  const [occupationLevelIds, setOccupationLevelIds] = useState(OccupationalLevelDisplayList());
  const [raceIds, setRaceIds] = useState(RaceList);
  const [genderIds, setGenderIds] = useState(GenderList);
  const [isStacked, setIsStacked] = useState(props.chartOptions.isStacked ?? true);
  const [counter, setCounter] = useState(0);
  const [anchorElFilter, setAnchorElFilter] = React.useState<null | HTMLElement>(null);
  const [openFilter, setOpenFilter] = React.useState(false);

  const chartKey = `${counter}-${JSON.stringify(props.sourceScorecardIds ?? [])}`;

  const filterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElFilter(anchorElFilter ? null : event.currentTarget);
    setOpenFilter(true);
  };
  
  const onChartSegmentClick = (finalLabel: string) => {
    if (props.dashboardLevelOnClick) {
      props.dashboardLevelOnClick(finalLabel);
      return;
    }
    //Do Custom stuff with Click
  }

  const changeStackedView = () => {
    setIsStacked(isStacked ? false : true);
  }

  const updateGenderFilter = (selectedGenderFilterOptions: Array<IMultiSelectOptions>) => {
    setGenderIds(selectedGenderFilterOptions);
    setCounter(prev => prev + 1);
  }

  const updateRaceFilter = (selectedRaceFilterOptions: Array<IMultiSelectOptions>) => {
    setRaceIds(selectedRaceFilterOptions);
    setCounter(prev => prev + 1);
  }

  const updateOccupationLevelFilter = (selectedOccupationLevelFilterOptions: Array<IMultiSelectOptions>) => {
    setOccupationLevelIds(selectedOccupationLevelFilterOptions);
    setCounter(prev => prev + 1);
  }

  const resetChartFilters = () => {
    setOccupationLevelIds(OccupationalLevelDisplayList());
    setRaceIds(RaceList);
    setGenderIds(GenderList);
    setAnchorElFilter(null);
    setOpenFilter(false);
    setCounter(prev => prev + 1);
  }

  const closeChartFilter = () => {
    setAnchorElFilter(null);
    setOpenFilter(false);
}

  const getChartData = (filteredDate: Date) => {
    let SelectedGenderArray: Array<number> = [];
    let SelectedRaceArray: Array<number> = [];
    let SelectedOccupationalLevelArray: Array<number> = [];
    genderIds.map((item) => SelectedGenderArray.push(parseInt(item.value)));
    raceIds.map((item) => SelectedRaceArray.push(parseInt(item.value)));
    occupationLevelIds.map((item) => SelectedOccupationalLevelArray.push(parseInt(item.value)));
    if(SelectedOccupationalLevelArray.includes(OccupationalLevelTypes.Top_Management)){
      SelectedOccupationalLevelArray = SelectedOccupationalLevelArray.filter(function(item) {
          return item !== OccupationalLevelTypes.Top_Management
      });
      SelectedOccupationalLevelArray.push(OccupationalLevelTypes.Executive_Director);
      SelectedOccupationalLevelArray.push(OccupationalLevelTypes.Other_Executive_Management);
    }
    const data: EmploymentEquityDashboardDto.EmployeeHeadcountRequest = {
      occupationLevelIds: SelectedOccupationalLevelArray,
      raceIds: SelectedRaceArray,
      genderIds: SelectedGenderArray,
      filterDate: filteredDate,
      scorecardId: props.scorecardId,
      sourceScorecardIds: props.sourceScorecardIds ? props.sourceScorecardIds : undefined
    }
    return DashboardService.GetEmployeeHeadCount(data).then(
      mapEmployeeHeadcountToDoubleColumnChartData(raceIds, genderIds, occupationLevelIds, props.chartOptions.isPowerPointPrint)
    );

  }

  const renderFilters = () => {
    return <MDBox style={{ float: 'right' }}>
      <IconButton
        color="primary"
        onClick={filterClick} >
        <FilterAltIcon />
      </IconButton>
      {openFilter && <ClickAwayListener onClickAway={() => closeChartFilter()}>
        <Popper
          open={openFilter}
          anchorEl={anchorElFilter}
          placement="bottom-end"
        >
          <Box sx={{
            bgcolor: 'background.paper',
            boxShadow: 2,
            borderRadius: 3,
            p: 2,
            border: '1px solid rgba(0, 0, 0, 0.25)',
            maxWidth: 530,
            minWidth: 400
          }}>
            <ChartFilter
              resetChartFilters={() => { resetChartFilters() }}
              enableResetFilter={true}
              dropdowns={[
                {
                  label: "Gender",
                  options: GenderList,
                  selected: genderIds,
                  onChange: updateGenderFilter,
                },
                {
                  label: "Race",
                  options: RaceList,
                  selected: raceIds,
                  onChange: updateRaceFilter,
                },
                {
                  label: "Occupational Level",
                  options: OccupationalLevelDisplayList(),
                  selected: occupationLevelIds,
                  onChange: updateOccupationLevelFilter,
                },
              ]}
            ></ChartFilter>
          </Box>
        </Popper>
      </ClickAwayListener>
      }
    </MDBox>
  }

  const filterDate = props?.chartOptions?.filterDate ?? getDateWithoutTime(new Date());
  const newHash = filterDate?.toISOString();
  return (
    <>
      <MDBox>
        <MDBox sx={{ margin: 1 }}>
          {renderFilters()}
          <MDButton color='primary' style={{ float: 'right' }}
            onClick={() => { changeStackedView() }}
          >
            {isStacked ? "Column View" : "Stacked View"}
          </MDButton>
          <br />
          <br />
          {
            genderIds.length > 0 &&
            raceIds.length > 0 &&
            occupationLevelIds.length > 0 && (
              <DoubleColumnChart
                key={chartKey}
                hash={newHash}
                chartOptions={props.chartOptions}
                fetchData={() => getChartData(filterDate)}
                onSegmentClick={onChartSegmentClick}
                isStacked={isStacked}
              />
            )
          }
          {
            props.chartOptions?.filterDate != null &&
            <span className="bottom-of-chart inline-block"><i style={{ color: 'black' }}>as at </i> &nbsp;<b>{userFriendlyDate(props.chartOptions?.filterDate)}</b></span>
          }
          {
            genderIds.length === 0 && (
              <AlertMessage
                type="danger"
                message="Please select at least one Gender"
              />
            )}
          {raceIds.length === 0 && (
            <AlertMessage
              type="danger"
              message="Please select at least one Race"
            />
          )
          }
          {
            occupationLevelIds.length === 0 && (
              <AlertMessage
                type="danger"
                message="Please select at least one Occupational Level"
              />
            )
          }
        </MDBox>
      </MDBox>
    </>
  )
}

